import React, { useState } from 'react';
import './index.css';

/**
 * BattleShipBoardProps interface defines the props for the BattleShipBoard component.
 *
 * @board - 2D array representing the board state, where each cell can be 'SHIP', 'X', 'O', or ''.
 * @isPlayerBoard - Boolean indicating if this board belongs to the current player.
 * @onCellClick - Optional callback function to handle cell clicks, used for firing at the opponent's board.
 */
interface BattleShipBoardProps {
  board: string[][]; // 2D grid representing the board state
  isPlayerBoard: boolean; // Whether this board belongs to the current player
  onCellClick?: (row: number, col: number) => void; // Callback when firing at opponent's board
}

/**
 * BattleShipBoard is a React functional component that renders a Battleship game board.
 *
 * @param board - The board for the BattleShipBoard component.
 * @param isPlayerBoard - Indicates if the board belongs to the player.
 * @param onCellClick - Optional callback function for handling cell clicks.
 * @returns A React functional component representing a Battleship game board.
 */
const BattleShipBoard: React.FC<BattleShipBoardProps> = ({ board, isPlayerBoard, onCellClick }) => {
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);

  return (
    <div className={`battleship-board ${isPlayerBoard ? 'player-board' : 'opponent-board'}`}>
      {board.map((row, rowIndex) => (
        <div key={rowIndex} className='board-row'>
          {row.map((cell, colIndex) => {
            let displayValue = cell;
            let isClickable = false;

            if (cell === 'X') {
              displayValue = '💥'; // Hit
            } else if (cell === 'O') {
              displayValue = '🌊'; // Miss
            } else if (cell === 'SHIP' && !isPlayerBoard) {
              displayValue = ''; // Hide opponent's ships
            } else if (cell === 'SHIP' && isPlayerBoard) {
              displayValue = '🛥️'; // Show own ships
            } else {
              displayValue = ''; // Empty cell
            }

            if (!isPlayerBoard && cell !== 'X' && cell !== 'O') {
              isClickable = true;
            }

            const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;

            return (
              <div
                key={colIndex}
                className={`board-cell ${cell} ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (!isPlayerBoard && isClickable && onCellClick) {
                    setSelectedCell({ row: rowIndex, col: colIndex });
                    onCellClick(rowIndex, colIndex);
                  }
                }}>
                {displayValue}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default BattleShipBoard;
